// Reduced motion (SPEC §15, "Accessibility is not negotiable").
//
// "Provide a reduced-motion mode that keeps the arcs but removes the tracer
// animation, scanlines, flicker, and aberration; a lot of people cannot use the
// full effect and they are also the people most likely to be playing for four
// weeks straight."
//
// Two things follow from that sentence and both are load-bearing:
//
//   THE ARCS STAY. Reduced motion is not "turn the war off". The arcs carry who
//   is attacking whom, which is the map's only legend; a reduced-motion player
//   who cannot see the fronts is playing a different, worse game. So `arcs` is
//   true in both modes and there is no switch that turns it off.
//
//   `prefers-reduced-motion` IS THE DEFAULT, not a setting buried in a panel. The
//   player who needs it has already told the browser, and asking twice is asking
//   them to find a menu in an interface built to be hard to read.

/** What is on and what is off. `arcs` is true in both modes, by design. */
export interface MotionFlags {
  /** The §15 ballistic arc layer. Never disabled. */
  arcs: boolean;
  /** The tracer travelling the curve, and its decaying tail. */
  tracer: boolean;
  /** The slow scanline sweep across panels. */
  scanlines: boolean;
  /** The single-frame flicker on value changes. */
  flicker: boolean;
  /** The chromatic-aberration ghost on region transitions. */
  aberration: boolean;
}

export const FullMotion: MotionFlags = {
  arcs: true,
  tracer: true,
  scanlines: true,
  flicker: true,
  aberration: true,
};

export const ReducedMotion: MotionFlags = {
  arcs: true,
  tracer: false,
  scanlines: false,
  flicker: false,
  aberration: false,
};

export function motionFlags(reduced: boolean): MotionFlags {
  return reduced ? ReducedMotion : FullMotion;
}

/** The narrow slice of `window.matchMedia` this module needs, so it can be faked. */
export interface MediaQuery {
  matches: boolean;
  addEventListener?(type: 'change', fn: (e: { matches: boolean }) => void): void;
  removeEventListener?(type: 'change', fn: (e: { matches: boolean }) => void): void;
  /** Safari before 14. Kept because a player on an old iPad is exactly the case this is for. */
  addListener?(fn: (e: { matches: boolean }) => void): void;
  removeListener?(fn: (e: { matches: boolean }) => void): void;
}

export type MatchMedia = (query: string) => MediaQuery;

export const ReducedMotionQuery = '(prefers-reduced-motion: reduce)';

/** Where the player's own preference is stored once they override the browser's. */
export const MotionOverrideKey = 'wwp.motion';

export interface MotionStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The browser's answer, or false where there is no browser.
 *
 * Defaulting to false without a matchMedia is right for the two places it happens
 * — a unit test and a server render — and wrong nowhere, because the real value
 * arrives on the first frame in a browser that has the API.
 */
export function prefersReducedMotion(mm?: MatchMedia): boolean {
  const impl = mm ?? (typeof globalThis.matchMedia === 'function' ? globalThis.matchMedia : null);
  if (!impl) return false;
  try {
    return impl(ReducedMotionQuery).matches === true;
  } catch {
    return false;
  }
}

/**
 * The effective setting: the player's explicit choice if they made one, otherwise
 * the browser's.
 *
 * Order matters. A stored 'full' has to be able to beat a system preference,
 * because someone may want the full effect on this one machine; a stored value is
 * only ever written by the player pressing the control.
 */
export function resolveReducedMotion(store?: MotionStore | null, mm?: MatchMedia): boolean {
  const saved = readOverride(store);
  if (saved !== null) return saved;
  return prefersReducedMotion(mm);
}

export function readOverride(store?: MotionStore | null): boolean | null {
  if (!store) return null;
  let v: string | null = null;
  try {
    v = store.getItem(MotionOverrideKey);
  } catch {
    return null;
  }
  if (v === 'reduced') return true;
  if (v === 'full') return false;
  return null;
}

export function writeOverride(store: MotionStore | null | undefined, reduced: boolean | null): void {
  if (!store) return;
  try {
    store.setItem(MotionOverrideKey, reduced === null ? 'system' : reduced ? 'reduced' : 'full');
  } catch {
    // A private window with storage disabled is not a reason to fail a render.
  }
}

/**
 * Watches the system preference. Returns an unsubscribe.
 *
 * The preference can change mid-session — an OS accessibility toggle, a plugged-in
 * external display profile — and a four-week season is long enough for that to
 * happen while the tab is open.
 */
export function watchReducedMotion(fn: (reduced: boolean) => void, mm?: MatchMedia): () => void {
  const impl = mm ?? (typeof globalThis.matchMedia === 'function' ? globalThis.matchMedia : null);
  if (!impl) return () => {};
  let q: MediaQuery;
  try {
    q = impl(ReducedMotionQuery);
  } catch {
    return () => {};
  }
  const handler = (e: { matches: boolean }): void => fn(e.matches === true);
  if (typeof q.addEventListener === 'function') {
    q.addEventListener('change', handler);
    return () => q.removeEventListener?.('change', handler);
  }
  if (typeof q.addListener === 'function') {
    q.addListener(handler);
    return () => q.removeListener?.(handler);
  }
  return () => {};
}
